import React, { memo, useEffect, useState } from 'react';
import { MenuPlacement } from 'react-select';

import { Datepicker, Input, NumberInput, Select, SliderCheckbox } from '@components/common';

import { useAppDispatch } from '@store/store';

import { changeDefaultValue, toggleIsUseDefaultValue } from '@slices/importMapData';

import { isValidDate } from '@utils/date';
import { fieldsOptionsFormatted } from '@utils/formatter';

import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	TCommonValue,
	TSelectedField,
} from '@interfaces/import/import';

import { EDefaultValueType } from '@enums/common';

import { booleanOptions } from '@constants/common';

import styles from './CustomValue.module.scss';

const MAX_TOOLTIP_LENGTH = 22;
const DEFAULT_PLACEHOLDER = 'Enter default value';

interface ICustomValueProps {
	field: TSelectedField;
	menuPlacement: MenuPlacement;
	isCommonDirty: boolean;
	isWithSlider?: boolean;
	customChangeHandler?: (value: {
		fieldName: string;
		defaultValue: TCommonValue | null;
	}) => void;
}

const CustomValue = ({
	field,
	menuPlacement,
	isCommonDirty,
	isWithSlider = true,
	customChangeHandler,
}: ICustomValueProps) => {
	const dispatch = useAppDispatch();

	const [isDirty, setIsDirty] = useState(false);

	const maxSize = field.systemFieldsDefaultData?.maxSize;
	const isErrorRequired = field.isRequired && !field.defaultValue && field.isUseDefaultValue && isDirty;

	const onBlur = () => setIsDirty(true);

	useEffect(() => setIsDirty(isCommonDirty), [isCommonDirty]);

	const onChangeCustomValueHandler = (value: string | boolean | number) => {
		const isBoolean = typeof value === 'boolean';
		const isDate = typeof value === 'number';

		if (maxSize && !isBoolean && !isDate && (maxSize < value.length)) return;

		const defaultValue = isBoolean || isDate || value === null
			? value
			: value.length
				? value
				: null;

		const customData = { fieldName: field.systemFieldName, defaultValue };

		if (customChangeHandler) customChangeHandler(customData);
		else dispatch(changeDefaultValue(customData));
	};

	const clearCustomValue = () => {
		const customData = { fieldName: field.systemFieldName, defaultValue: null };

		if (customChangeHandler) customChangeHandler(customData);
		else dispatch(changeDefaultValue(customData));
	};

	const getFieldByType = () => {
		switch (field?.systemFieldsDefaultData?.defaultValueType) {
			case EDefaultValueType.array:
			case EDefaultValueType.arrayCreatable: {
				const options = fieldsOptionsFormatted(field.systemFieldsDefaultData?.options || []);
				const isCreatable = field.systemFieldsDefaultData.defaultValueType ===
					EDefaultValueType.arrayCreatable;
				const isExistDefaultValue = field.defaultValue !== null && field.defaultValue !== undefined;
				const selectValue = options.find(option => option.value === field.defaultValue) || (
					isCreatable && isExistDefaultValue
						? { value: field.defaultValue, label: field.defaultValue }
						: null
				);

				const onChangeSelectHandler = (option: TSelectOption<string>) => {
					onChangeCustomValueHandler(option?.value as string);
				};

				return (
					<Select
						disabled={!field.isUseDefaultValue}
						handleChange={onChangeSelectHandler}
						isCreatable={isCreatable}
						isError={isErrorRequired}
						isWithoutUseForms
						menuPlacement={menuPlacement}
						menuPortalTarget={document.body}
						name={`${field.systemFieldName}${field.title}`}
						onBlur={onBlur}
						options={options}
						placeholder='Select default value'
						value={selectValue}
						width='240px'
					/>
				);
			}
			case EDefaultValueType.date: {
				const dateValue = (typeof field.defaultValue === 'number' && isValidDate(field.defaultValue)) ? new Date(field.defaultValue) : undefined;
				const onChangeDatepickerHandler = (date: number | null) => {
					if (!date) return;

					onChangeCustomValueHandler(date);
				};

				return (
					<Datepicker
						disableInput={!field.isUseDefaultValue}
						disabled={!field.isUseDefaultValue}
						handleChange={onChangeDatepickerHandler}
						isError={isErrorRequired}
						isFullWidth
						isMobileDevice={false}
						selected={dateValue}
					/>
				);
			}
			case EDefaultValueType.boolean: {
				const onChangeSelectHandler = (option: TSelectOption<boolean>) => {
					onChangeCustomValueHandler(option?.value as boolean);
				};

				const booleanValue = booleanOptions.find(option => option.value === field.defaultValue) || null;

				return (
					<Select
						disabled={!field.isUseDefaultValue}
						handleChange={onChangeSelectHandler}
						isError={isErrorRequired}
						isWithoutUseForms
						menuPlacement={menuPlacement}
						menuPortalTarget={document.body}
						name={`${field.systemFieldName}${field.title}`}
						onBlur={onBlur}
						options={booleanOptions}
						placeholder='Select default value'
						value={booleanValue}
						width='240px'
					/>
				);
			}
			case EDefaultValueType.digits: {
				const maxValue = field.systemFieldsDefaultData?.maxValue;
				const maxChars = field.systemFieldsDefaultData?.maxSize;
				const isErrorMaxValue = !!field.defaultValue && field.isUseDefaultValue && isDirty
					&& !!maxValue && (Number(field.defaultValue) > maxValue);
				const rangeText = maxValue
					? `Set value from 0 to ${maxValue}`
					: DEFAULT_PLACEHOLDER;
				const rangeErrorText = `Must be from 0 to ${maxValue}`;

				return (
					<NumberInput
						disabled={!field.isUseDefaultValue}
						errorMessage={isErrorMaxValue ? rangeErrorText : undefined}
						isError={isErrorRequired || isErrorMaxValue}
						maxChars={maxChars}
						maxValue={maxValue}
						name={`${field.systemFieldName}${field.title}`}
						onBlur={onBlur}
						onChange={onChangeCustomValueHandler}
						placeholder={rangeText}
						step={1}
						value={field.defaultValue as string || ''}
						withTooltip={!!maxValue && (rangeText.length > MAX_TOOLTIP_LENGTH)}
					/>
				);
			}
			case EDefaultValueType.string:
				return (
					<Input
						disabled={!field.isUseDefaultValue}
						isError={isErrorRequired}
						name={`${field.systemFieldName}${field.title}`}
						onBlur={onBlur}
						onChange={onChangeCustomValueHandler}
						placeholder={DEFAULT_PLACEHOLDER}
						value={field.defaultValue as string || ''}
						withUseForms={false}
					/>
				);
			default:
				return <div>Unknown type</div>;
		}
	};
	const toggleCustomValue = () => {
		if (isWithSlider) {
			dispatch(toggleIsUseDefaultValue({ fieldName: field.systemFieldName }));
		}

		if (field.isUseDefaultValue) clearCustomValue();

		if (isCommonDirty) setIsDirty(!field.isUseDefaultValue);
	};

	return (
		<div className={styles.customValue}>
			{isWithSlider &&
				<SliderCheckbox
					checked={field.isUseDefaultValue}
					id={`${field.systemFieldName}${field.title}${field.example}}`}
					onChange={toggleCustomValue}
				/>
			}
			<div className={styles.width}>{getFieldByType()}</div>
		</div>
	);
};

export default memo(CustomValue);
